import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Sample from "../types/sample";
import http from "../api/http";

interface SampleState {
  sample?: Sample;
  status: "idle" | "loading" | "failed";
  loggedIn?: boolean;
}

const initialState: SampleState = {
  sample: undefined,
  status: "idle",
};

export const fetchSample = createAsyncThunk<Sample>(
  "sample/fetch",
  async () => {
    const response = await http.get<Sample>("/settings");
    return response.data;
  }
);

export const randomCameraOffline = createAsyncThunk(
  "sample/random-camera",
  async () => {
    const response = await http.post("/demo/camera");
    return response.data;
  }
);

export const randomDoorForce = createAsyncThunk(
  "sample/random-force",
  async () => {
    const response = await http.post("/demo/door-force");
    return response.data;
  }
);

export const randomDoorHeld = createAsyncThunk(
  "sample/random-hold",
  async () => {
    const response = await http.post("/demo/door-held");
    return response.data;
  }
);

export const resetCameraOffline = createAsyncThunk(
  "sample/reset-camera",
  async () => {
    const response = await http.patch("/demo/camera/reset");
    return response.data;
  }
);

export const resetDoorForce = createAsyncThunk(
  "sample/reset-force",
  async () => {
    const response = await http.patch("/demo/door-force/reset");
    return response.data;
  }
);

export const resetDoorHeld = createAsyncThunk("sample/reset-hold", async () => {
  const response = await http.patch("/demo/door-held/reset");
  return response.data;
});

export const cameraOffline = createAsyncThunk(
  "sample/camera",
  async (id: number) => {
    const response = await http.patch(`/demo/camera/${id}`);
    return response.data;
  }
);

export const doorForce = createAsyncThunk(
  "sample/force",
  async (id: string) => {
    const response = await http.patch(`/demo/door-force/${id}`);
    return response.data;
  }
);

export const doorHeld = createAsyncThunk("sample/hold", async (id: string) => {
  const response = await http.patch(`/demo/door-held/${id}`);
  return response.data;
});

export const generateOmnilert = createAsyncThunk(
  "sample/omnilert",
  async () => {
    const response = await http.post("/demo/weapon");
    return response.data;
  }
);

const sampleSlice = createSlice({
  name: "sample",
  initialState,
  reducers: {
    clearSample(state) {
      state.sample = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSample.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchSample.fulfilled, (state, action) => {
        state.status = "idle";
        state.sample = action.payload;
      })
      .addCase(fetchSample.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(randomCameraOffline.pending, (state) => {
        state.status = "loading";
      })
      .addCase(randomCameraOffline.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(randomCameraOffline.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(randomDoorForce.pending, (state) => {
        state.status = "loading";
      })
      .addCase(randomDoorForce.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(randomDoorForce.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(randomDoorHeld.pending, (state) => {
        state.status = "loading";
      })
      .addCase(randomDoorHeld.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(randomDoorHeld.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(cameraOffline.pending, (state) => {
        state.status = "loading";
      })
      .addCase(cameraOffline.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(cameraOffline.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(doorForce.pending, (state) => {
        state.status = "loading";
      })
      .addCase(doorForce.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(doorForce.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(doorHeld.pending, (state) => {
        state.status = "loading";
      })
      .addCase(doorHeld.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(doorHeld.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(generateOmnilert.pending, (state) => {
        state.status = "loading";
      })
      .addCase(generateOmnilert.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(generateOmnilert.rejected, (state, action) => {
        state.status = "failed";
      });
  },
});

export const { clearSample } = sampleSlice.actions;

export default sampleSlice.reducer;
